import { useMutation, useQueryClient } from 'react-query'
import { useSnackbar } from 'notistack'
import { createZone } from 'services/cm-api/create-zone'
import { ErrorResponseT } from 'types'

/**
 * Wraps `POST /global-data/create-zone`. On success it invalidates the
 * global-zones list so the new zone appears in the table and in every
 * zone-assignment dropdown. Callers may pass `onSuccess`/`onError` to drive
 * their own (localized) snackbars and dialog close; otherwise English
 * fallbacks are used.
 */
export function useCreateZone({
  onSuccess,
  onError,
}: { onSuccess?: Function; onError?: Function } = {}) {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(createZone, {
    onSuccess: async (response) => {
      if (response.success) {
        await queryClient.invalidateQueries(['getGlobalZones'])
        if (onSuccess) {
          onSuccess(response.data)
        } else {
          enqueueSnackbar('Custom zone created', { variant: 'success' })
        }
        return
      }
      if (onError) {
        onError((response as ErrorResponseT).error)
      } else {
        enqueueSnackbar('Failed to create custom zone', { variant: 'error' })
      }
    },
    onError: (err) => {
      if (onError) {
        onError(err)
      } else {
        enqueueSnackbar('Failed to create custom zone', { variant: 'error' })
      }
      console.error('createZone failed: ', err)
    },
  })
}
